import { useState, useEffect, useMemo } from 'react'
import type { EmailThreadSummary, EmailMessage, Contact } from '../../types'
import { useAuthStore } from '../../stores/authStore'
import { useGmailStore } from '../../stores/gmailStore'
import { gmailService } from '../../services/gmail'
import { Button } from '../ui'

interface CompanyEmailHistoryProps {
  contacts: Contact[]
  title?: string
}

export default function CompanyEmailHistory({ 
  contacts, 
  title = 'Emails' 
}: CompanyEmailHistoryProps) {
  const { hasGmailScope, connectGmail } = useAuthStore()
  const { fetchThreadsForContact, loadingContacts } = useGmailStore()
  const [allThreads, setAllThreads] = useState<EmailThreadSummary[]>([])
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const contactsWithEmail = useMemo(
    () => contacts.filter((c) => c.email),
    [contacts]
  )

  const contactEmails = useMemo(
    () => contactsWithEmail.map((c) => c.email!.toLowerCase().trim()),
    [contactsWithEmail]
  )

  useEffect(() => {
    if (hasGmailScope && contactEmails.length > 0) {
      loadAllThreads()
    }
  }, [hasGmailScope, contactEmails.join(',')])

  const loadAllThreads = async () => {
    if (contactEmails.length === 0) return

    setIsLoading(true)
    setError(null)

    try {
      const threadArrays = await Promise.all(
        contactEmails.map((email) => fetchThreadsForContact(email))
      )

      const threadMap = new Map<string, EmailThreadSummary>()
      threadArrays.flat().forEach((thread) => {
        if (!threadMap.has(thread.id)) {
          threadMap.set(thread.id, thread)
        }
      })

      const uniqueThreads = Array.from(threadMap.values()).sort(
        (a, b) => b.lastMessageDate.getTime() - a.lastMessageDate.getTime()
      )

      setAllThreads(uniqueThreads)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch emails')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnectGmail = async () => {
    setIsConnecting(true)
    try {
      await connectGmail()
    } finally {
      setIsConnecting(false)
    }
  }

  const handleRefresh = () => {
    contactEmails.forEach((email) => {
      useGmailStore.getState().clearContactCache(email)
    })
    loadAllThreads()
  }

  const isAnyLoading = isLoading || contactEmails.some((email) => 
    loadingContacts.has(email)
  )

  if (contactsWithEmail.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <div className="empty-state py-6">
          <p className="empty-state-text">No contacts with email addresses.</p>
        </div>
      </div>
    )
  }

  if (!hasGmailScope) {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <div className="bg-[#F5F5F3] rounded-lg p-4 text-center">
          <p className="text-sm text-text-secondary mb-3">
            Connect Gmail to see email history with {contactsWithEmail.length} contact{contactsWithEmail.length !== 1 ? 's' : ''}.
          </p>
          <p className="text-xs text-text-secondary mb-4">
            Emails are read directly from Gmail, not stored in the app.
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConnectGmail}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting...' : 'Connect Gmail'}
          </Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>
            Retry
          </Button>
        </div>
        <div className="bg-[#FAEDEC] rounded-lg p-4">
          <p className="text-sm text-error">Couldn't load emails. Try refreshing.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isAnyLoading}>
          {isAnyLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {contactsWithEmail.length > 1 && (
        <p className="text-xs text-text-secondary">
          Showing emails from {contactsWithEmail.length} contacts
        </p>
      )}

      {isAnyLoading && allThreads.length === 0 ? (
        <div className="empty-state py-6">
          <p className="empty-state-text">Loading emails...</p>
        </div>
      ) : allThreads.length === 0 ? (
        <div className="empty-state py-6">
          <p className="empty-state-text">No email history found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {allThreads.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isExpanded={expandedThreadId === thread.id}
              onToggle={() =>
                setExpandedThreadId(
                  expandedThreadId === thread.id ? null : thread.id
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ThreadItemProps {
  thread: EmailThreadSummary
  isExpanded: boolean
  onToggle: () => void
}

function ThreadItem({ thread, isExpanded, onToggle }: ThreadItemProps) {
  const { fetchFullThread, loadingThreads } = useGmailStore()
  const [messages, setMessages] = useState<EmailMessage[]>([])

  const isLoadingMessages = loadingThreads.has(thread.id)

  useEffect(() => {
    if (isExpanded && messages.length === 0) {
      loadMessages()
    }
  }, [isExpanded])

  const loadMessages = async () => {
    const result = await fetchFullThread(thread.id)
    setMessages(result)
  }

  const formatDate = (date: Date) => {
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const isThisYear = date.getFullYear() === now.getFullYear()

    if (isToday) {
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    if (isThisYear) {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
      })
    }

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const handleOpenInGmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.open(gmailService.getGmailThreadUrl(thread.id), '_blank')
  }

  return (
    <div className="bg-[#F5F5F3] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-3 text-left hover:bg-[#EEEEEC] transition-colors duration-150"
        aria-expanded={isExpanded}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-sm truncate ${
                  thread.isUnread ? 'font-semibold' : ''
                }`}
              >
                {thread.subject}
              </span>
              {thread.messageCount > 1 && (
                <span className="text-xs text-text-secondary flex-shrink-0">
                  ({thread.messageCount})
                </span>
              )}
            </div>
            <p className="text-xs text-text-secondary truncate">{thread.snippet}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-text-secondary">
              {formatDate(thread.lastMessageDate)}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`text-text-secondary transition-transform duration-150 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-border">
          {isLoadingMessages && messages.length === 0 ? (
            <div className="p-3 text-center">
              <p className="text-sm text-text-secondary">Loading messages...</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-border">
                {messages.map((message) => (
                  <MessageItem key={message.id} message={message} />
                ))}
              </div>
              <div className="p-2 bg-bg-surface border-t border-border">
                <button
                  onClick={handleOpenInGmail}
                  className="text-xs text-action-primary hover:underline"
                >
                  Open in Gmail
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

interface MessageItemProps {
  message: EmailMessage
}

function MessageItem({ message }: MessageItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const parseFromAddress = (from: string): { name: string; email: string } => {
    const match = from.match(/^(.+?)\s*<([^>]+)>$/)
    if (match && match[1] && match[2]) {
      return { name: match[1].replace(/^"|"$/g, ''), email: match[2] }
    }
    return { name: from, email: from }
  }

  const { name, email } = parseFromAddress(message.from)

  return (
    <div className="p-3 bg-bg-surface">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm font-medium text-text-primary truncate">
            {name}
          </span>
          <span className="text-xs text-text-secondary flex-shrink-0">
            {formatDate(message.date)}
          </span>
        </div>
        {!isExpanded && (
          <p className="text-xs text-text-secondary truncate">{message.snippet}</p>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="text-xs text-text-secondary mb-2 space-y-1">
            <p>
              <span className="font-medium">From:</span> {name} &lt;{email}&gt;
            </p>
            {message.to.length > 0 && (
              <p>
                <span className="font-medium">To:</span> {message.to.join(', ')}
              </p>
            )}
            {message.cc.length > 0 && (
              <p>
                <span className="font-medium">Cc:</span> {message.cc.join(', ')}
              </p>
            )}
          </div>
          <div className="text-sm text-text-primary whitespace-pre-wrap break-words">
            {message.bodyText || message.snippet}
          </div>
        </div>
      )}
    </div>
  )
}
